import React, { useState } from 'react';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameMonth, 
  isToday, 
  isEqual, 
  addDays, 
  subDays, 
  startOfWeek, 
  endOfWeek, 
  addMonths, 
  subMonths, 
  addWeeks, 
  subWeeks,
  getDay,
  getDate,
  parseISO
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { useThemeStore } from '../stores/themeStore';
import { CalendarDays, Calendar as CalendarIcon, LayoutGrid, ChevronLeft, ChevronRight, Clock } from 'lucide-react';

type ViewMode = 'month' | 'week' | 'day';

export interface Appointment {
  id: string;
  title: string;
  date: Date;
  time: string;
  agency: string;
  contact: string;
  priority: 'high' | 'medium' | 'normal';
  userId: string;
  alertDate?: Date;
  alertType?: 'email' | 'notification' | 'both';
  alertMinutes?: number;
  suivi?: string;
}

interface CalendarViewProps {
  currentDate: Date;
  selectedDate: Date | null;
  onDateSelect: (date: Date) => void;
  appointments?: Appointment[];
  onAppointmentClick?: (appointment: Appointment) => void;
  onTimeSlotClick?: (date: Date, hour: number) => void;
  alerts?: Array<{
    date: Date;
    status: 'pending' | 'completed' | 'late';
  }>;
}

function CalendarView({ 
  currentDate, 
  selectedDate, 
  onDateSelect, 
  appointments = [], 
  onAppointmentClick,
  onTimeSlotClick,
  alerts = [] 
}: CalendarViewProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const { darkMode } = useThemeStore();

  // Définir les jours de la semaine en commençant par lundi
  const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  
  // Calcul des dates selon la vue
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  
  // Calcul pour la vue semaine
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  const daysInWeek = eachDayOfInterval({ start: weekStart, end: weekEnd });
  
  // Obtenir les jours du mois avec les jours du mois précédent/suivant pour compléter les semaines
  const getMonthDays = () => {
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    
    // Obtenir le premier jour du mois (0 = dimanche, 1 = lundi, etc.)
    let firstDayOfMonth = getDay(monthStart);
    // Ajuster pour commencer par lundi
    firstDayOfMonth = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    
    // Ajouter les jours du mois précédent
    const prevMonthDays = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      prevMonthDays.unshift(subDays(monthStart, i + 1));
    }
    
    // Calculer combien de jours il faut ajouter pour compléter la dernière semaine
    const totalDays = prevMonthDays.length + days.length;
    const remainingDays = (7 - (totalDays % 7)) % 7;
    
    // Ajouter les jours du mois suivant
    const nextMonthDays = [];
    for (let i = 1; i <= remainingDays; i++) {
      nextMonthDays.push(addDays(monthEnd, i));
    }
    
    return [...prevMonthDays, ...days, ...nextMonthDays];
  };

  const calendarDays = getMonthDays();

  const getDateStatus = (date: Date) => {
    const alertsForDate = alerts.filter(alert => 
      isEqual(new Date(alert.date), date)
    );
    
    if (alertsForDate.length === 0) return null;
    
    if (alertsForDate.some(alert => alert.status === 'late')) {
      return 'late';
    }
    if (alertsForDate.some(alert => alert.status === 'pending')) {
      return 'pending';
    }
    return 'completed';
  };

  const getStatusStyles = (status: string | null) => {
    switch (status) {
      case 'late':
        return 'border-2 border-red-500';
      case 'pending':
        return 'border-2 border-orange-500';
      case 'completed':
        return 'border-2 border-green-500';
      default:
        return 'border border-transparent';
    }
  };

  // Navigation entre les périodes
  const navigatePrevious = () => {
    switch (viewMode) {
      case 'month':
        onDateSelect(subMonths(currentDate, 1));
        break;
      case 'week':
        onDateSelect(subWeeks(currentDate, 1));
        break;
      case 'day':
        onDateSelect(subDays(currentDate, 1));
        break;
    }
  };

  const navigateNext = () => {
    switch (viewMode) {
      case 'month':
        onDateSelect(addMonths(currentDate, 1));
        break;
      case 'week':
        onDateSelect(addWeeks(currentDate, 1));
        break;
      case 'day':
        onDateSelect(addDays(currentDate, 1));
        break;
    }
  };

  // Formatage du titre selon la vue
  const getViewTitle = () => {
    switch (viewMode) {
      case 'month':
        return format(currentDate, 'MMMM yyyy', { locale: fr });
      case 'week':
        return `Semaine du ${format(weekStart, 'd MMMM', { locale: fr })} au ${format(weekEnd, 'd MMMM yyyy', { locale: fr })}`;
      case 'day':
        return format(currentDate, "EEEE d MMMM yyyy", { locale: fr });
    }
  };

  // Obtenir les rendez-vous pour une date et une heure spécifiques
  const getAppointmentsForDateTime = (date: Date, hour?: number) => {
    return appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      const sameDay = 
        aptDate.getDate() === date.getDate() && 
        aptDate.getMonth() === date.getMonth() && 
        aptDate.getFullYear() === date.getFullYear();
      
      if (!hour) return sameDay;
      
      // Si une heure est spécifiée, vérifier si le rendez-vous est à cette heure
      const aptHour = parseInt(apt.time.split(':')[0]);
      return sameDay && aptHour === hour;
    });
  };

  // Rendu de la vue jour
  const renderDayView = () => {
    const dayStatus = getDateStatus(currentDate);
    const timeSlots = Array.from({ length: 13 }, (_, i) => i + 8); // 8h à 20h
    
    return (
      <div className="flex flex-col">
        <div className={`
          w-full p-4 mb-2 rounded-lg text-center font-medium
          ${isToday(currentDate) ? (darkMode ? 'bg-blue-900/10' : 'bg-blue-50') : ''}
          ${getStatusStyles(dayStatus)}
        `}>
          <div className="text-lg">{format(currentDate, 'd', { locale: fr })}</div>
          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {format(currentDate, 'EEEE', { locale: fr })}
          </div>
        </div>
        
        <div className={`border rounded-lg overflow-hidden ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {timeSlots.map(hour => {
            const aptsForHour = getAppointmentsForDateTime(currentDate, hour);
            
            return (
              <div 
                key={hour} 
                className={`flex border-b last:border-b-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}
              >
                <div className={`w-16 p-2 text-right text-sm border-r ${darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                  {hour}:00
                </div>
                <div 
                  className={`flex-1 p-2 min-h-[60px] ${darkMode ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'} cursor-pointer`}
                  onClick={() => onTimeSlotClick && onTimeSlotClick(currentDate, hour)}
                >
                  {aptsForHour.map(apt => (
                    <div 
                      key={apt.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAppointmentClick && onAppointmentClick(apt);
                      }}
                      className={`
                        p-2 rounded-lg mb-1 cursor-pointer
                        ${apt.priority === 'high' ? (darkMode ? 'bg-red-900 text-red-100' : 'bg-red-100 text-red-800') : 
                          apt.priority === 'medium' ? (darkMode ? 'bg-orange-900 text-orange-100' : 'bg-orange-100 text-orange-800') : 
                          (darkMode ? 'bg-blue-900 text-blue-100' : 'bg-blue-100 text-blue-800')}
                      `}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{apt.title}</span>
                        <span className="text-xs">{apt.time}</span>
                      </div>
                      <div className="text-xs">{apt.contact} - {apt.agency}</div>
                      {apt.suivi && <div className="text-xs mt-1">Suivi: {apt.suivi}</div>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Rendu de la vue semaine
  const renderWeekView = () => {
    const timeSlots = Array.from({ length: 13 }, (_, i) => i + 8); // 8h à 20h
    
    return (
      <div className="flex flex-col">
        <div className="grid grid-cols-8 gap-px mb-2">
          {/* Cellule vide pour l'en-tête des heures */}
          <div className=""></div>
          
          {/* En-têtes des jours */}
          {daysInWeek.map((date) => {
            const isSelected = selectedDate ? isEqual(date, selectedDate) : false;
            const isCurrentDay = isToday(date);
            
            return (
              <div 
                key={date.toString()} 
                className={`text-center cursor-pointer ${isCurrentDay ? (darkMode ? 'text-blue-300' : 'text-blue-600') : ''}`}
                onClick={() => onDateSelect(date)}
              >
                <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {format(date, 'EEE', { locale: fr })}
                </div>
                <div className={`
                  w-8 h-8 mx-auto rounded-full flex items-center justify-center text-sm
                  ${isSelected ? 'bg-blue-600 text-white' : ''}
                  ${isCurrentDay && !isSelected ? (darkMode ? 'bg-blue-900/30' : 'bg-blue-50') : ''}
                `}>
                  {getDate(date)}
                </div>
              </div>
            );
          })}
        </div>
        
        <div className={`border rounded-lg overflow-hidden ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {timeSlots.map(hour => (
            <div 
              key={hour} 
              className={`flex border-b last:border-b-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}
            >
              <div className={`w-16 p-2 text-right text-sm border-r ${darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                {hour}:00
              </div>
              
              {/* Cellules pour chaque jour */}
              {daysInWeek.map(day => {
                const aptsForHour = getAppointmentsForDateTime(day, hour);
                
                return (
                  <div 
                    key={day.toString()} 
                    className={`flex-1 p-1 min-h-[40px] border-r last:border-r-0 ${
                      darkMode ? 'border-gray-700 hover:bg-gray-700/30' : 'border-gray-200 hover:bg-gray-50'
                    } ${isToday(day) ? (darkMode ? 'bg-blue-900/10' : 'bg-blue-50/30') : ''} cursor-pointer`}
                    onClick={() => onTimeSlotClick && onTimeSlotClick(day, hour)}
                  >
                    {aptsForHour.map(apt => (
                      <div 
                        key={apt.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          onAppointmentClick && onAppointmentClick(apt);
                        }}
                        className={`
                          p-1 rounded text-xs mb-1 cursor-pointer truncate
                          ${apt.priority === 'high' ? (darkMode ? 'bg-red-900 text-red-100' : 'bg-red-100 text-red-800') : 
                            apt.priority === 'medium' ? (darkMode ? 'bg-orange-900 text-orange-100' : 'bg-orange-100 text-orange-800') : 
                            (darkMode ? 'bg-blue-900 text-blue-100' : 'bg-blue-100 text-blue-800')}
                        `}
                      >
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span>{apt.title}</span>
                        </div>
                        {apt.suivi && <div className="text-xs truncate">Suivi: {apt.suivi}</div>}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Rendu de la vue mois
  const renderMonthView = () => {
    return (
      <div className="w-full">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map((day) => (
            <div
              key={day}
              className={`text-center text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} py-2`}
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((date) => {
            const isSelected = selectedDate ? isEqual(date, selectedDate) : false;
            const isCurrentMonth = isSameMonth(date, currentDate);
            const isCurrentDay = isToday(date);
            const status = getDateStatus(date);
            const aptsForDay = getAppointmentsForDateTime(date);
            const hasAppointments = aptsForDay.length > 0;

            return (
              <button
                key={date.toString()}
                onClick={() => onDateSelect(date)}
                className={`
                  relative h-14 rounded-lg text-sm font-medium transition-all
                  ${!isCurrentMonth && (darkMode ? 'text-gray-600' : 'text-gray-300')}
                  ${isSelected ? 'bg-blue-600 text-white border-transparent' : getStatusStyles(status)}
                  ${isCurrentDay && !isSelected && (darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-50 text-blue-600')}
                  ${isCurrentMonth && !isSelected && !isCurrentDay && (darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50')}
                `}
              >
                <span className="absolute top-1 left-1/2 -translate-x-1/2 w-full">
                  {format(date, 'd')}
                </span>
                
                {hasAppointments && !isSelected && (
                  <div className="absolute bottom-1 left-0 right-0 flex justify-center">
                    <div className={`flex gap-0.5 ${aptsForDay.length > 3 ? 'w-full justify-center' : ''}`}>
                      {aptsForDay.length <= 3 ? (
                        aptsForDay.map((apt, idx) => (
                          <span 
                            key={idx} 
                            className={`w-1.5 h-1.5 rounded-full ${
                              apt.priority === 'high' ? 'bg-red-500' : 
                              apt.priority === 'medium' ? 'bg-orange-500' : 
                              'bg-blue-500'
                            }`}
                          />
                        ))
                      ) : (
                        <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {aptsForDay.length} RDV
                        </span>
                      )}
                    </div>
                  </div>
                )}
                
                {status && !isSelected && (
                  <span className={`
                    absolute top-1 right-1 w-2 h-2 rounded-full
                    ${status === 'late' ? 'bg-red-500' : 
                      status === 'pending' ? 'bg-orange-500' : 
                      'bg-green-500'}
                  `} />
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={darkMode ? 'text-white' : 'text-gray-900'}>
      {/* En-tête avec navigation et sélecteur de vue */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-2">
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigatePrevious();
            }}
            className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          >
            <ChevronLeft className={`w-5 h-5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
          </button>
          <h2 className="text-lg font-medium">{getViewTitle()}</h2>
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigateNext();
            }}
            className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          >
            <ChevronRight className={`w-5 h-5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
          </button>
        </div>
        
        <div className={`flex gap-1 border ${darkMode ? 'border-gray-700' : 'border-gray-200'} rounded-lg p-1`}>
          <button
            onClick={() => setViewMode('day')}
            className={`p-2 rounded-lg ${viewMode === 'day' ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}`}
            title="Vue par jour"
          >
            <CalendarIcon className={`w-5 h-5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
          </button>
          <button
            onClick={() => setViewMode('week')}
            className={`p-2 rounded-lg ${viewMode === 'week' ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}`}
            title="Vue par semaine"
          >
            <LayoutGrid className={`w-5 h-5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
          </button>
          <button
            onClick={() => setViewMode('month')}
            className={`p-2 rounded-lg ${viewMode === 'month' ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}`}
            title="Vue par mois"
          >
            <CalendarDays className={`w-5 h-5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
          </button>
        </div>
      </div>

      {/* Affichage du calendrier selon la vue sélectionnée */}
      {viewMode === 'day' && renderDayView()}
      {viewMode === 'week' && renderWeekView()}
      {viewMode === 'month' && renderMonthView()}

      <div className="mt-4 flex items-center justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full border-2 border-red-500"></span>
          <span className={darkMode ? "text-gray-300" : "text-gray-600"}>En retard</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full border-2 border-orange-500"></span>
          <span className={darkMode ? "text-gray-300" : "text-gray-600"}>En attente</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full border-2 border-green-500"></span>
          <span className={darkMode ? "text-gray-300" : "text-gray-600"}>Effectué</span>
        </div>
      </div>
    </div>
  );
}

export default CalendarView;